<template>
  <div v-if="showWorkSelector" class="work-selector p-learning-class">
    <h2 class="p-learning-class__title">教材</h2>
    <dl class="p-learning-filtering -no-gap">
      <select-single
        :options="workOptions"
        v-model:selected="proxyWorkId"
        @change="emit('change')"
        :initBlank="false"
      ></select-single>
    </dl>
  </div>
</template>

<script setup lang="ts">
  import { computed, watch } from "vue";
  import { SUBJECT_ALL, SUBJECT_MAP } from "@/consts/subject";
  import { JUNIOR_HIGH_ACADEMIC_YEARS } from "@/consts/academic_year";
  import { ISelectSingleOption } from "@/consts/interfaces";
  import { useWorkStore } from "@/store/teacher/work_store";

  const ALL_WORKS_OPTION: ISelectSingleOption = { value: '', name: 'すべて' }

  const props = defineProps<{
    academicSessionId: number | null;
    academicYear: number | string | null;
    subject: string;
    workId: number | string;
  }>()

  const emit = defineEmits<{
    (e: 'update:workId', value: number | string): void;
    (e: 'change'): void;
  }>()

  const workStore = useWorkStore()

  const proxyWorkId = computed({
    get() { return props.workId },
    set(value) { emit('update:workId', value) },
  })

  const showWorkSelector = computed(() => {
    return !!props.academicSessionId && isAcademicYearSet() && props.subject !== SUBJECT_ALL.value
  })

  const workOptions = computed((): ISelectSingleOption[] => {
    const subjects = workStore.availableWorksBySubject(props.academicSessionId as number, props.academicYear as number)
    if (!subjects) return [ALL_WORKS_OPTION]
    const found = subjects.find(s => SUBJECT_MAP[s.name] === props.subject)
    return [ALL_WORKS_OPTION, ...found.works.map(w => ({ value: w.id, name: w.name }))]
  })

  function isAcademicYearSet() {
    return JUNIOR_HIGH_ACADEMIC_YEARS.includes(props.academicYear as number)
  }

  function fetchAvailableWorks() {
    if (!isAcademicYearSet() || props.subject === SUBJECT_ALL.value || !props.academicSessionId) return
    workStore.fetchAvailableWorksBySubject({
      academic_session_id: props.academicSessionId,
      academic_year: props.academicYear as number,
      target_years_only: true,
    })
  }

  // academicSessionId は画面上で変更されることを想定していないが、
  // 親側 (T8-1) のonMountedの処理具合により、後から確定するケースがあるため、watch 対象に含める
  watch([() => props.academicSessionId, () => props.subject, () => props.academicYear], fetchAvailableWorks, { immediate: true })
</script>

<style lang="scss" scoped>
.work-selector {
  border-bottom: none;
  padding-bottom: 0;
}
</style>
